'use client'

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

// 类型定义
export interface User {
  id: string
  username: string
  phone: string
  email?: string
  password?: string
  canInquiry: boolean
  canExportQuote: boolean
  canAuditInquiry: boolean
  canAuditQuoteUser?: boolean
  canViewQuoteUsers?: boolean
  role: 'admin' | 'inquiry' | 'quote'
  status?: '暂存' | '审核中' | '已审核' | '已退回'
  isDisabled?: boolean
  createdAt: string
  companyFullName?: string
  businessLicense?: string
  otherAttachments?: string
  companyContactPerson?: string
  companyContactPhone?: string
}

export interface Inquiry {
  id: string
  inquiryNo: string
  createdAt: string
  createdBy: string
  materialName: string
  specification: string
  limitPrice: string
  inquiryQuantity?: string
  quoteRequirement: string
  attachment?: string
  status: '创建' | '审核中' | '重新审核' | '已审核'
  publishStatus: '已发布' | '未发布'
  publishTime?: string
  quoteDeadline: string
  deliveryDeadline?: string
  deliveryAddress?: string
  procurementMethodCode?: string
  isEnded?: boolean
}

export interface Quote {
  id: string
  quoteNo: string
  companyName: string
  contactPerson: string
  contactPhone: string
  quoteAmount?: number
  attachment?: string
  quoteTime: string
  inquiryNo: string
  userId: string
  status: '已报价' | '已审核'
  isSelected?: '中选' | '未中选' | '待定'
  selectedTime?: string
  remark?: string
  notificationSent?: boolean
  notificationSentTime?: string
  biddingRound?: number
  quoteResultDesc?: string
  // 中选通知书字段
  noticeQualityStandard?: string
  noticeRequirement1?: string
  noticeRequirement2?: string
  noticeContactName?: string
  noticeContactPhone?: string
  noticeContactEmail?: string
  noticeSignerCompany?: string
  noticeSignerPerson?: string
  noticeDate?: string
}

export interface ProcurementMethod {
  id: string
  methodCode: string
  methodName: string
  allowMultiRound: boolean
  createdAt?: string
}

export interface BiddingRound {
  id: string
  inquiryNo: string
  roundNumber: number
  quoteDeadline?: string
  biddingStatus: string
  businessStatus: string
}

interface AppState {
  currentUser: User | null
  users: User[]
  inquiries: Inquiry[]
  quotes: Quote[]
  procurementMethods: ProcurementMethod[]
  biddingRounds: BiddingRound[]
  isLoggedIn: boolean
  isLoading: boolean
  dbInitialized: boolean
}

interface AppContextType extends AppState {
  login: (account: string, password: string) => Promise<boolean>
  logout: () => void
  register: (phone: string, password: string, username: string, companyInfo?: {
    companyFullName: string; businessLicense: string;
    companyContactPerson: string; companyContactPhone: string;
  }) => Promise<boolean>
  addInquiry: (inquiry: Omit<Inquiry, 'id' | 'inquiryNo' | 'createdAt'>) => Promise<void>
  updateInquiry: (id: string, inquiry: Partial<Inquiry>) => Promise<void>
  deleteInquiry: (id: string) => Promise<void>
  addQuote: (quote: Omit<Quote, 'id' | 'quoteNo' | 'quoteTime'>) => Promise<boolean>
  updateQuote: (id: string, quote: Partial<Quote>) => Promise<void>
  deleteQuote: (id: string) => Promise<void>
  addUser: (user: Omit<User, 'id' | 'createdAt'>) => Promise<void>
  updateUser: (id: string, user: Partial<User>) => Promise<void>
  updateProfile: (profileData: Partial<User>) => Promise<any>
  deleteUser: (id: string) => Promise<void>
  refreshData: () => Promise<void>
  fetchProcurementMethods: () => Promise<void>
  fetchBiddingRounds: (inquiryNo?: string) => Promise<void>
  loadUsersWithFullPhone: () => Promise<void>
}

const AppContext = createContext<AppContextType | undefined>(undefined)

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>({
    currentUser: null,
    users: [],
    inquiries: [],
    quotes: [],
    procurementMethods: [],
    biddingRounds: [],
    isLoggedIn: false,
    isLoading: true,
    dbInitialized: false
  })

  // 初始化数据库并加载数据
  const initAndLoadData = useCallback(async () => {
    try {
      // 首先初始化数据库
      const initRes = await fetch('/api/init', { credentials: 'include' })
      const initData = await initRes.json()
      console.log('数据库初始化:', initData)

      // 并行加载采购方式和竞价轮次（这些接口始终公开）
      const [pmRes, brRes] = await Promise.all([
        fetch('/api/procurement-methods', { credentials: 'include' }),
        fetch('/api/bidding-rounds', { credentials: 'include' })
      ])
      const [pmData, brData] = await Promise.all([
        pmRes.json(),
        brRes.json()
      ])

      // 尝试加载需要认证的数据（用户列表、所有询价单、所有报价单）
      // 如果未登录，这些接口会返回错误，但我们仍然可以加载公开的已发布询价单和中选记录
      const [usersRes, inquiriesRes, quotesRes] = await Promise.all([
        fetch('/api/users', { credentials: 'include' }),
        fetch('/api/inquiries', { credentials: 'include' }),
        fetch('/api/quotes', { credentials: 'include' })
      ])

      const usersData = await usersRes.json()
      const inquiriesData = await inquiriesRes.json()
      const quotesData = await quotesRes.json()

      // 如果询价单或报价单加载失败（未登录），尝试加载公开数据
      let finalInquiries = inquiriesData.success ? inquiriesData.data : []
      let finalQuotes = quotesData.success ? quotesData.data : []

      if (!inquiriesData.success) {
        const publicInquiriesRes = await fetch('/api/inquiries?published=true', { credentials: 'include' })
        const publicInquiriesData = await publicInquiriesRes.json()
        finalInquiries = publicInquiriesData.success ? publicInquiriesData.data : []
      }

      if (!quotesData.success) {
        const publicQuotesRes = await fetch('/api/quotes?published=true', { credentials: 'include' })
        const publicQuotesData = await publicQuotesRes.json()
        finalQuotes = publicQuotesData.success ? publicQuotesData.data : []
      }

      setState(prev => ({
        ...prev,
        users: usersData.success ? usersData.data : [],
        inquiries: finalInquiries,
        quotes: finalQuotes,
        procurementMethods: pmData.success ? pmData.data : [],
        biddingRounds: brData.success ? brData.data : [],
        isLoading: false,
        dbInitialized: true
      }))
    } catch (error) {
      console.error('加载数据失败:', error)
      setState(prev => ({ ...prev, isLoading: false }))
    }
  }, [])

  // 刷新数据
  const refreshData = useCallback(async () => {
    try {
      // 尝试加载需要认证的数据
      const [usersRes, inquiriesRes, quotesRes, pmRes, brRes] = await Promise.all([
        fetch('/api/users', { credentials: 'include' }),
        fetch('/api/inquiries', { credentials: 'include' }),
        fetch('/api/quotes', { credentials: 'include' }),
        fetch('/api/procurement-methods', { credentials: 'include' }),
        fetch('/api/bidding-rounds', { credentials: 'include' })
      ])

      const usersData = await usersRes.json()
      const inquiriesData = await inquiriesRes.json()
      const quotesData = await quotesRes.json()
      const pmData = await pmRes.json()
      const brData = await brRes.json()

      // 如果询价单或报价单加载失败（未登录），尝试加载公开数据
      let finalInquiries = inquiriesData.success ? inquiriesData.data : []
      let finalQuotes = quotesData.success ? quotesData.data : []

      if (!inquiriesData.success) {
        const publicInquiriesRes = await fetch('/api/inquiries?published=true', { credentials: 'include' })
        const publicInquiriesData = await publicInquiriesRes.json()
        finalInquiries = publicInquiriesData.success ? publicInquiriesData.data : []
      }

      if (!quotesData.success) {
        const publicQuotesRes = await fetch('/api/quotes?published=true', { credentials: 'include' })
        const publicQuotesData = await publicQuotesRes.json()
        finalQuotes = publicQuotesData.success ? publicQuotesData.data : []
      }

      setState(prev => {
        const newUsers = usersData.success ? usersData.data : prev.users
        const updatedCurrentUser = prev.currentUser
          ? newUsers.find((u: User) => u.id === prev.currentUser!.id) || prev.currentUser
          : null
        return {
          ...prev,
          users: newUsers,
          inquiries: finalInquiries,
          quotes: finalQuotes,
          procurementMethods: pmData.success ? pmData.data : prev.procurementMethods,
          biddingRounds: brData.success ? brData.data : prev.biddingRounds,
          currentUser: updatedCurrentUser
        }
      })
    } catch (error) {
      console.error('刷新数据失败:', error)
    }
  }, [])

  // 获取采购方式
  const fetchProcurementMethods = useCallback(async () => {
    try {
      const res = await fetch('/api/procurement-methods', { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setState(prev => ({ ...prev, procurementMethods: data.data }))
      }
    } catch (error) {
      console.error('获取采购方式失败:', error)
    }
  }, [])

  // 获取竞价轮次
  const fetchBiddingRounds = useCallback(async (inquiryNo?: string) => {
    try {
      const url = inquiryNo ? `/api/bidding-rounds?inquiryNo=${inquiryNo}` : '/api/bidding-rounds'
      const res = await fetch(url)
      const data = await res.json()
      if (data.success) {
        if (inquiryNo) {
          setState(prev => ({
            ...prev,
            biddingRounds: [
              ...prev.biddingRounds.filter(r => r.inquiryNo !== inquiryNo),
              ...data.data
            ]
          }))
        } else {
          setState(prev => ({ ...prev, biddingRounds: data.data }))
        }
      }
    } catch (error) {
      console.error('获取竞价轮次失败:', error)
    }
  }, [])

  useEffect(() => {
    initAndLoadData()
  }, [initAndLoadData])

  // 登录 - 支持用户名或手机号
  const login = async (account: string, password: string): Promise<boolean> => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account: account.trim(), password: password.trim() })
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({ ...prev, currentUser: data.user, isLoggedIn: true }))
        return true
      }
      return false
    } catch (error) {
      console.error('登录失败:', error)
      return false
    }
  }

  // 登出
  const logout = () => {
    setState(prev => ({ ...prev, currentUser: null, isLoggedIn: false }))
  }

  // 注册
  const register = async (phone: string, password: string, username: string, companyInfo?: {
    companyFullName: string; businessLicense: string;
    companyContactPerson: string; companyContactPhone: string;
  }): Promise<boolean> => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone, password, username,
          companyFullName: companyInfo?.companyFullName || '',
          businessLicense: companyInfo?.businessLicense || '',
          companyContactPerson: companyInfo?.companyContactPerson || '',
          companyContactPhone: companyInfo?.companyContactPhone || ''
        })
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          currentUser: data.user,
          isLoggedIn: true,
          users: [...prev.users, data.user]
        }))
        return true
      }
      return false
    } catch (error) {
      console.error('注册失败:', error)
      return false
    }
  }

  // 新增询价单
  const addInquiry = async (inquiry: Omit<Inquiry, 'id' | 'inquiryNo' | 'createdAt'>) => {
    try {
      const res = await fetch('/api/inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inquiry)
      })
      const data = await res.json()
      
      if (data.success) {
        await refreshData()
      }
    } catch (error) {
      console.error('新增询价单失败:', error)
    }
  }

  // 更新询价单
  const updateInquiry = async (id: string, inquiry: Partial<Inquiry>) => {
    try {
      const currentInquiry = state.inquiries.find(i => i.id === id)
      const res = await fetch('/api/inquiries', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...currentInquiry, ...inquiry, id })
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          inquiries: prev.inquiries.map(i => i.id === id ? { ...i, ...inquiry } : i)
        }))
      }
    } catch (error) {
      console.error('更新询价单失败:', error)
    }
  }

  // 删除询价单
  const deleteInquiry = async (id: string) => {
    try {
      const res = await fetch(`/api/inquiries?id=${id}`, { method: 'DELETE' })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          inquiries: prev.inquiries.filter(i => i.id !== id)
        }))
      }
    } catch (error) {
      console.error('删除询价单失败:', error)
    }
  }

  // 新增报价单
  const addQuote = async (quote: Omit<Quote, 'id' | 'quoteNo' | 'quoteTime'>): Promise<boolean> => {
    try {
      console.log('正在提交报价单:', quote)
      const res = await fetch('/api/quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quote)
      })
      const data = await res.json()
      console.log('报价单API响应:', data)
      
      if (data.success) {
        await refreshData()
        return true
      } else {
        console.error('报价单保存失败:', data.message)
        return false
      }
    } catch (error) {
      console.error('新增报价单失败:', error)
      return false
    }
  }

  // 更新报价单
  const updateQuote = async (id: string, quote: Partial<Quote>) => {
    try {
      const currentQuote = state.quotes.find(q => q.id === id)
      const res = await fetch('/api/quotes', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...currentQuote, ...quote, id })
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          quotes: prev.quotes.map(q => q.id === id ? { ...q, ...quote } : q)
        }))
        return data
      } else {
        // 显示错误提示
        alert(data.message || '更新报价单失败')
        throw new Error(data.message || '更新报价单失败')
      }
    } catch (error) {
      console.error('更新报价单失败:', error)
      throw error
    }
  }

  // 删除报价单
  const deleteQuote = async (id: string) => {
    try {
      const res = await fetch(`/api/quotes?id=${id}`, { method: 'DELETE' })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          quotes: prev.quotes.filter(q => q.id !== id)
        }))
        return data
      } else {
        // 显示错误提示
        alert(data.message || '删除报价单失败')
        throw new Error(data.message || '删除报价单失败')
      }
    } catch (error) {
      console.error('删除报价单失败:', error)
      throw error
    }
  }

  // 新增用户
  const addUser = async (user: Omit<User, 'id' | 'createdAt'>) => {
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(user)
      })
      const data = await res.json()
      
      if (data.success) {
        await refreshData()
      } else {
        throw new Error(data.message || '新增用户失败')
      }
    } catch (error) {
      console.error('新增用户失败:', error)
      throw error
    }
  }

  // 更新用户（管理员功能）
  const updateUser = async (id: string, user: Partial<User>) => {
    try {
      const res = await fetch('/api/users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...user, id })
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          users: prev.users.map(u => u.id === id ? { ...u, ...user } : u),
          // 如果更新的是当前登录用户，同步更新 currentUser
          currentUser: prev.currentUser?.id === id 
            ? { ...prev.currentUser, ...user } 
            : prev.currentUser
        }))
      } else {
        throw new Error(data.message || '更新用户失败')
      }
    } catch (error) {
      console.error('更新用户失败:', error)
      throw error
    }
  }

  // 更新个人信息（用户自己的信息）
  const updateProfile = async (profileData: Partial<User>) => {
    try {
      const res = await fetch('/api/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          currentUser: data.user || { ...prev.currentUser, ...profileData }
        }))
        return data
      }
      return data
    } catch (error) {
      console.error('更新个人信息失败:', error)
      throw error
    }
  }

  // 删除用户
  const deleteUser = async (id: string) => {
    try {
      const res = await fetch(`/api/users?id=${id}`, { method: 'DELETE' })
      const data = await res.json()
      
      if (data.success) {
        setState(prev => ({
          ...prev,
          users: prev.users.filter(u => u.id !== id)
        }))
      }
    } catch (error) {
      console.error('删除用户失败:', error)
    }
  }

  // 加载完整手机号的用戶列表
  const loadUsersWithFullPhone = useCallback(async () => {
    try {
      const res = await fetch('/api/users?fullPhone=true', { credentials: 'include' })
      const data = await res.json()
      if (data.success) {
        setState(prev => ({ ...prev, users: data.data }))
      }
    } catch (error) {
      console.error('加载用户列表失败:', error)
    }
  }, [])

  return (
    <AppContext.Provider value={{
      ...state,
      login,
      logout,
      register,
      addInquiry,
      updateInquiry,
      deleteInquiry,
      addQuote,
      updateQuote,
      deleteQuote,
      addUser,
      updateUser,
      updateProfile,
      deleteUser,
      refreshData,
      fetchProcurementMethods,
      fetchBiddingRounds,
      loadUsersWithFullPhone
    }}>
      {children}
    </AppContext.Provider>
  )
}

export function useApp() {
  const context = useContext(AppContext)
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}
